import { useState } from 'react'
import { useToast } from '../hooks/useToast'
import {
  accountStatusLabel, callAccountAction,
  type AccountActionResponse, type AccountCheck, type NameConfirmed, type SavedAccountCheck,
} from '../lib/boxtopAccount'

// Z102 PR1: the customer -> BoxTop account code, shown, verifiable and fixable in place. Used on the Customer
// page and as the Step 1 card of the Sync Preflight, so both behave the same.
//
// Flow: [Verify] re-checks the saved code live. [Change / Enter code] checks a typed code FIRST (read only);
// only a code that resolves can be saved. BoxTop's company name is shown beside ours as a HINT: names that are
// not identical are flagged and staff tick "same company" (remembered for that account, so no re-asking) --
// the name never blocks anything; only "BoxTop has no such account" does. Replacing a different saved code needs
// an explicit tick. Nothing here ever falls back to our own customer code.

interface Props {
  customerId: string
  customerName: string
  customerCode: string | null
  accountNumber: string | null
  saved?: SavedAccountCheck | null
  /** Verdict of a check that just ran (e.g. the preflight's own Step 1 call). */
  lastCheck?: AccountCheck | null
  /** Staff already confirmed "same company despite different names" for the CURRENT account. */
  nameConfirmed?: NameConfirmed | null
  lastChange?: { at: string; by: string | null } | null
  onChanged: () => void
}

const TONE: Record<string, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  unverified: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  none: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
}

export default function BoxtopAccountPanel({ customerId, customerName, customerCode, accountNumber, saved, lastCheck, nameConfirmed, lastChange, onChanged }: Props) {
  const { toast } = useToast()
  const [busy, setBusy] = useState<'verify' | 'check' | 'save' | null>(null)
  const [verifyResult, setVerifyResult] = useState<AccountActionResponse | null>(null)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [candidate, setCandidate] = useState<AccountActionResponse | null>(null)
  const [candidateError, setCandidateError] = useState<string | null>(null)
  const [okOverwrite, setOkOverwrite] = useState(false)
  const [okName, setOkName] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const status = accountStatusLabel(accountNumber, saved)
  const shown = verifyResult?.check ?? lastCheck ?? null

  const verifySaved = async () => {
    setBusy('verify'); setVerifyResult(null)
    const r = await callAccountAction('verify_boxtop_account', { customer_id: customerId })
    setVerifyResult(r)
    setBusy(null)
    if (r.success) onChanged()
  }

  const checkTyped = async () => {
    const code = value.trim().toUpperCase()
    if (!code) return
    setBusy('check'); setCandidate(null); setCandidateError(null); setSaveError(null); setOkOverwrite(false); setOkName(false)
    const r = await callAccountAction('verify_boxtop_account', { customer_id: customerId, account_number: code })
    setBusy(null)
    if (!r.success || !r.check) { setCandidateError(r.error || 'Check failed'); return }
    if (!r.check.ok) { setCandidateError(r.check.message); return }   // an unverified code can never be saved
    setCandidate(r)
  }

  const save = async () => {
    if (!candidate?.proposed) return
    setBusy('save'); setSaveError(null)
    const r = await callAccountAction('bind_boxtop_account', {
      customer_id: customerId,
      account_number: candidate.proposed,
      confirm_overwrite: okOverwrite,
      confirm_name_mismatch: okName,
    })
    setBusy(null)
    if (!r.success) { setSaveError(r.error || 'Saving failed'); return }   // never report success unless the server confirmed the write
    toast(`BoxTop account ${candidate.proposed} saved for ${customerName}`, 'success')
    setEditing(false); setValue(''); setCandidate(null); setVerifyResult(null)
    onChanged()
  }

  const mismatch = candidate?.nameMatch?.status === 'different'
  const similar = candidate?.nameMatch?.status === 'similar'
  // Hint only: wording/colour differ by status, the rule is the same -- a not-identical name needs one human tick,
  // unless staff already confirmed it for this very account.
  const nameNotSame = !!candidate?.nameMatch && candidate.nameMatch.status !== 'same'
  const needNameTick = nameNotSame && !candidate?.nameConfirmed
  const canSave = !!candidate && busy === null && (!candidate.willOverwrite || okOverwrite) && (!needNameTick || okName)

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="text-gray-500 dark:text-gray-400">Our customer: <span className="text-gray-900 dark:text-white font-medium">{customerName}</span> <span className="font-mono text-xs">({customerCode ?? '—'})</span></span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-gray-500 dark:text-gray-400">BoxTop account code:</span>
        <span className="font-mono font-semibold text-gray-900 dark:text-white">{accountNumber ?? '—'}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TONE[status.tone]}`}>{status.text}</span>
        {saved?.status === 'ok' && saved.checkedNumber === accountNumber && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {saved.companyName ?? '—'} · {saved.addressCount ?? '?'} addresses{saved.checkedBy ? ` · by ${saved.checkedBy}` : ''}
          </span>
        )}
      </div>
      {nameConfirmed && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Name difference confirmed as the same company{nameConfirmed.by ? ` by ${nameConfirmed.by}` : ''}{nameConfirmed.at ? ` on ${new Date(nameConfirmed.at).toISOString().slice(0, 10)}` : ''}
          {nameConfirmed.boxtopName ? ` (BoxTop name: ${nameConfirmed.boxtopName})` : ''}.
        </p>
      )}
      <p className="text-xs text-gray-400">
        Stored in customers.boxtop_account_number (this customer's own record)
        {lastChange ? ` · last changed ${new Date(lastChange.at).toISOString().slice(0, 16).replace('T', ' ')}${lastChange.by ? ` by ${lastChange.by}` : ''}` : ''}
      </p>

      {shown && (
        <div className={`p-2 rounded-lg text-xs ${shown.ok ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'}`}>
          {shown.ok ? '✓ ' : '✗ '}{shown.message}
          {!shown.ok && shown.sentAccountNumber && <div className="mt-0.5 font-mono">CustAccNum sent: {shown.sentAccountNumber}{shown.httpStatus != null ? ` · HTTP ${shown.httpStatus}` : ''}</div>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {accountNumber && (
          <button type="button" onClick={verifySaved} disabled={busy !== null}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            {busy === 'verify' ? 'Verifying…' : 'Verify'}
          </button>
        )}
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} disabled={busy !== null} className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
            {accountNumber ? 'Change BoxTop code' : 'Enter BoxTop code'}
          </button>
        )}
      </div>

      {editing && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input value={value} onChange={e => { setValue(e.target.value.toUpperCase()); setCandidate(null); setCandidateError(null) }}
              placeholder="BoxTop account, e.g. LANDR" maxLength={8} disabled={busy !== null}
              className="text-sm font-mono border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-2 py-1 w-44" />
            <button type="button" onClick={checkTyped} disabled={busy !== null || !value.trim()}
              className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium disabled:opacity-50">
              {busy === 'check' ? 'Checking…' : 'Check with BoxTop'}
            </button>
            <button type="button" onClick={() => { setEditing(false); setValue(''); setCandidate(null); setCandidateError(null); setSaveError(null) }} disabled={busy === 'save'}
              className="text-xs text-gray-500 hover:underline">Cancel</button>
          </div>
          {candidateError && <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-xs">✗ {candidateError} Not saved.</div>}

          {candidate?.check?.ok && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
                  <p className="text-gray-500 uppercase font-semibold">Ours</p>
                  <p className="text-gray-900 dark:text-white">{candidate.ourName}</p>
                </div>
                <div className={`border rounded-md p-2 ${mismatch ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : similar ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' : 'border-green-400 bg-green-50 dark:bg-green-900/20'}`}>
                  <p className="text-gray-500 uppercase font-semibold">BoxTop · {candidate.proposed} · {candidate.check.addressCount} addresses</p>
                  <p className="text-gray-900 dark:text-white">{candidate.check.boxtopCompanyName ?? '(no company name)'}</p>
                </div>
              </div>
              {needNameTick && (
                <label className={`flex items-start gap-2 text-xs ${mismatch ? 'text-red-700 dark:text-red-400' : 'text-amber-800 dark:text-amber-300'}`}>
                  <input type="checkbox" checked={okName} onChange={e => setOkName(e.target.checked)} className="mt-0.5" />
                  <span><strong>{similar ? 'Names are similar but not identical' : 'Names look different'} ({candidate.nameMatch?.scorePct}%) — please confirm this is the same company.</strong> BoxTop often stores a short name; you decide.</span>
                </label>
              )}
              {nameNotSame && candidate.nameConfirmed && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Names differ, but this was already confirmed as the same company{candidate.nameConfirmed.by ? ` by ${candidate.nameConfirmed.by}` : ''}{candidate.nameConfirmed.at ? ` on ${new Date(candidate.nameConfirmed.at).toISOString().slice(0, 10)}` : ''} — no need to confirm again.
                </p>
              )}
              {candidate.willOverwrite && (
                <label className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300">
                  <input type="checkbox" checked={okOverwrite} onChange={e => setOkOverwrite(e.target.checked)} className="mt-0.5" />
                  <span>This customer already has BoxTop account <strong className="font-mono">{candidate.current}</strong>. Replace it with <strong className="font-mono">{candidate.proposed}</strong>.</span>
                </label>
              )}
              {saveError && <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-xs">✗ {saveError}</div>}
              <button type="button" onClick={save} disabled={!canSave}
                className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md font-medium disabled:opacity-50">
                {busy === 'save' ? 'Saving…' : `Save ${candidate.proposed} to ${customerName}`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
